/**
 * Reusable address block: Search Address or Places + City, State, Landmark, Pin only.
 * Uses Nominatim (OSM) for place search; on select fills all fields.
 */
import React, { useState, useEffect } from 'react';
import { Box, TextField, Autocomplete, Typography, CircularProgress } from '@mui/material';

const defaultAddressShape = {
  city: '',
  state: '',
  country: 'IN',
  pincode: '',
  locality: '',
  address_line: '',
  google_maps_url: '',
  latitude: null,
  longitude: null,
  google_place_id: '',
};

const fieldStylesDefault = {
  '& .MuiInputBase-root': { height: 40, fontSize: 12, borderRadius: 1 },
  '& .MuiInputLabel-root': { fontSize: 12, color: '#1a1a1a', '&.Mui-focused': { color: '#1a1a1a' } },
  '& .MuiInputBase-input::placeholder': { fontSize: 12, opacity: 0.7 },
};

function mapPlaceFromNominatim(place) {
  const addr = place.address || {};
  const city = addr.city || addr.town || addr.village || addr.municipality || '';
  const state = addr.state || addr.region || '';
  const locality =
    addr.suburb ||
    addr.neighbourhood ||
    addr.quarter ||
    addr.village ||
    addr.locality ||
    city ||
    (place.display_name && place.display_name.split(',')[0]) ||
    '';
  const road = addr.road || '';
  const houseNumber = addr.house_number || '';
  const address_line =
    [houseNumber, road].filter(Boolean).join(' ') ||
    locality ||
    (place.display_name && place.display_name.split(',')[0]) ||
    '';
  const pincode = addr.postcode || '';
  const lat = place.lat != null ? parseFloat(place.lat) : null;
  const lon = place.lon != null ? parseFloat(place.lon) : null;
  return {
    placeId: String(place.place_id),
    display_name: place.display_name || '',
    city,
    state: state || '',
    country: addr.country || 'India',
    locality,
    address_line,
    pincode,
    latitude: Number.isFinite(lat) ? lat : null,
    longitude: Number.isFinite(lon) ? lon : null,
  };
}

export default function CompleteAddress({
  value = {},
  onChange,
  error = null,
  fieldStyles = fieldStylesDefault,
  placeholder = 'e.g. Cama Industrial Estate, Goregaon East, Mumbai',
  label = 'Search Address or Places',
  helperText = 'Type area, landmark or full address — select to fill',
  disabled = false,
}) {
  const address = { ...defaultAddressShape, ...value };
  const landmark = address.address_line || '';
  const localityValue = address.locality || '';

  const [addressSearchInput, setAddressSearchInput] = useState('');
  const [addressSearchOptions, setAddressSearchOptions] = useState([]);
  const [addressSearchLoading, setAddressSearchLoading] = useState(false);

  const updateAddress = (partial) => {
    onChange?.({ ...address, ...partial });
  };

  useEffect(() => {
    const trimmed = addressSearchInput.trim();
    if (!trimmed) {
      setAddressSearchOptions([]);
      return;
    }
    const timeoutId = setTimeout(() => {
      setAddressSearchLoading(true);
      const queryForRequest = trimmed.includes('India') ? trimmed : `${trimmed}, India`;
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(queryForRequest)}&format=json&addressdetails=1&limit=15&countrycodes=in`;

      fetch(url, { headers: { 'User-Agent': 'SalesGro App' } })
        .then((r) => r.json())
        .then((data) => {
          if (data && data.length > 0) {
            setAddressSearchOptions(data.map(mapPlaceFromNominatim));
            setAddressSearchLoading(false);
            return;
          }
          const tokens = trimmed.split(/[\s,]+/).filter(Boolean);
          const fallbackQuery = tokens.length > 1 ? `${tokens[tokens.length - 1]}, India` : null;
          if (!fallbackQuery || fallbackQuery === queryForRequest) {
            setAddressSearchOptions([]);
            setAddressSearchLoading(false);
            return;
          }
          const fallbackUrl = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(fallbackQuery)}&format=json&addressdetails=1&limit=15&countrycodes=in`;
          return fetch(fallbackUrl, { headers: { 'User-Agent': 'SalesGro App' } })
            .then((r2) => r2.json())
            .then((data2) => {
              setAddressSearchLoading(false);
              if (data2 && data2.length > 0) {
                setAddressSearchOptions(data2.map(mapPlaceFromNominatim));
              } else {
                setAddressSearchOptions([]);
              }
            })
            .catch(() => {
              setAddressSearchLoading(false);
              setAddressSearchOptions([]);
            });
        })
        .catch(() => {
          setAddressSearchLoading(false);
          setAddressSearchOptions([]);
        });
    }, 320);
    return () => clearTimeout(timeoutId);
  }, [addressSearchInput]);

  const handleSearchSelect = (_, newValue) => {
    if (!newValue || typeof newValue === 'string') {
      if (typeof newValue === 'string' && newValue.trim()) {
        updateAddress({ address_line: newValue.trim(), locality: newValue.trim() });
      }
      return;
    }
    setAddressSearchInput(newValue.display_name || '');
    updateAddress({
      city: newValue.city || '',
      state: newValue.state || '',
      country: newValue.country || 'IN',
      locality: newValue.locality || '',
      address_line: newValue.address_line || newValue.locality || '',
      pincode: newValue.pincode || '',
      google_place_id: newValue.placeId || '',
      latitude: newValue.latitude != null ? newValue.latitude : null,
      longitude: newValue.longitude != null ? newValue.longitude : null,
    });
    setAddressSearchOptions([]);
  };

  const handleSearchBlur = () => {
    const trimmed = addressSearchInput.trim();
    if (trimmed && !addressSearchOptions.some((o) => o.display_name === trimmed)) {
      updateAddress({ address_line: trimmed, locality: trimmed });
    }
  };

  const handleLandmarkChange = (v) => {
    updateAddress({ address_line: v });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Autocomplete
        freeSolo
        fullWidth
        size="small"
        disabled={disabled}
        options={addressSearchOptions}
        loading={addressSearchLoading}
        inputValue={addressSearchInput}
        onInputChange={(_, newValue) => setAddressSearchInput(newValue ?? '')}
        value={null}
        onChange={handleSearchSelect}
        onBlur={handleSearchBlur}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.display_name || ''
        }
        isOptionEqualToValue={(option, val) =>
          !val ? false : option.placeId === val?.placeId
        }
        filterOptions={(x) => x}
        renderOption={(props, option) => (
          <li {...props} key={option.placeId}>
            <Box sx={{ py: 0.5 }}>
              <Typography variant="body2" fontWeight={500}>
                {option.locality || (option.display_name && option.display_name.split(',')[0])}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {[option.address_line, option.city, option.state, option.pincode]
                  .filter(Boolean)
                  .join(', ')}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            error={!!error}
            helperText={error || helperText}
            sx={fieldStyles}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {addressSearchLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            InputLabelProps={{ shrink: true }}
          />
        )}
        noOptionsText={
          addressSearchLoading
            ? 'Searching…'
            : addressSearchInput.trim()
              ? 'No places found. Try a shorter search (e.g. area or city name).'
              : 'Type area, street or full address'
        }
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
          '& > *': { minWidth: 0 },
        }}
      >
        <TextField
          fullWidth
          label="City"
          value={address.city ?? ''}
          onChange={(e) => updateAddress({ city: e.target.value })}
          size="small"
          sx={fieldStyles}
          InputLabelProps={{ shrink: true }}
          disabled={disabled}
        />
        <TextField
          fullWidth
          label="State"
          value={address.state ?? ''}
          onChange={(e) => updateAddress({ state: e.target.value })}
          size="small"
          sx={fieldStyles}
          InputLabelProps={{ shrink: true }}
          disabled={disabled}
        />
      </Box>

      <TextField
        fullWidth
        required
        label="Google Maps link"
        placeholder="Paste a Google Maps URL for this location"
        value={address.google_maps_url || ''}
        onChange={(e) => updateAddress({ google_maps_url: e.target.value })}
        size="small"
        sx={fieldStyles}
        InputLabelProps={{ shrink: true }}
        disabled={disabled}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1.2fr 1fr 140px',
          gap: 2,
          '& > *': { minWidth: 0 },
        }}
      >
        <TextField
          fullWidth
          label="Landmark"
          value={landmark}
          onChange={(e) => handleLandmarkChange(e.target.value)}
          size="small"
          sx={fieldStyles}
          InputLabelProps={{ shrink: true }}
          disabled={disabled}
        />
        <TextField
          fullWidth
          label="Locality"
          value={localityValue}
          onChange={(e) => updateAddress({ locality: e.target.value })}
          size="small"
          sx={fieldStyles}
          InputLabelProps={{ shrink: true }}
          disabled={disabled}
        />
        <TextField
          label="Pin"
          value={address.pincode ?? ''}
          inputProps={{ maxLength: 6 }}
          onChange={(e) => updateAddress({ pincode: e.target.value })}
          size="small"
          sx={fieldStyles}
          InputLabelProps={{ shrink: true }}
          disabled={disabled}
        />
      </Box>
    </Box>
  );
}

export { defaultAddressShape, fieldStylesDefault };
